import PropTypes from 'prop-types';
import { price as priceUtil } from '@eti/utils';
import { css, etiColors, mediaQueries } from '@eti/styles';
import { useSiteContext, useTranslation } from '@eti/providers';

const groupStyles = css`
  border-top: 1px dotted #aaa;
  margin-top: 0.5rem;
  padding-top: 8px;
`;

const itemStyles = (isDark) => css`
  align-items: flex-end;
  display: flex;
  font-size: 0.75rem;
  justify-content: space-between;
  ${isDark ? `color: ${etiColors.black}` : `color: ${etiColors.grayScale[10]}`};
`;

const labelStyles = css`
  color: ${etiColors.black};
  font-size: 0.75rem;
  font-weight: 600;
`;

const taxesAndFeesStyles = css`
  margin: 8px 0;
`;

const taxesAndFeesCategoryLabelStyles = css`
  color: ${etiColors.grayScale[10]};
  font-size: 0.75rem;
  font-weight: 600;
`;

const taxesAndFeesPriceStyles = css`
  margin-left: 8px;
  white-space: nowrap;
`;

const vatLabelStyles = css`
  flex: 0 0 75%;

  @media ${mediaQueries.large.up} {
    flex: 0 0 70%;
  }
`;

const vatPriceStyles = css`
  text-align: right;

  html[dir='rtl'] & {
    text-align: left;
  }
`;

const isShowingPrice = (price) => price !== null;

const shouldShowTaxBreakdown = (taxesAndFees) => taxesAndFees && taxesAndFees.length > 0;

const ReceiptTravelers = ({
  dataTestId,
  totalSumLabel,
  travelerPricesInfo = [],
  viewMode = '',
}) => {
  const { market } = useSiteContext();
  const { t } = useTranslation();
  return (
    <div data-testid={dataTestId}>
      {travelerPricesInfo.map(
        ({
          ageType,
          ageTypeLabel,
          costLabel,
          discountLabel,
          markup,
          price,
          priceLabel,
          totalLabel,
          totalPrice,
          totalPriceSum,
          vat,
          vatLabel,
          taxesAndFees,
        }) => {
          return (
            <div
              className={groupStyles}
              data-testid={`traveler-price-info${viewMode}`}
              key={ageType}
            >
              <div className={labelStyles}>{ageTypeLabel}</div>
              {shouldShowTaxBreakdown(taxesAndFees) && (
                <div className={taxesAndFeesStyles}>
                  {taxesAndFees.map(
                    ({ category, taxes }) =>
                      taxes.length > 0 && (
                        <div key={category}>
                          {market.code !== 'US' && (
                            <h4 className={taxesAndFeesCategoryLabelStyles}>
                              {t(`Cart.TaxBreakdown.Category.${category}`)}
                            </h4>
                          )}
                          {taxes.map(
                            ({ title, amount: { value: amountValue } }) =>
                              amountValue !== 0 && (
                                <div className={itemStyles()} key={`${category}-${title}`}>
                                  <span>{title}</span>
                                  <span
                                    className={taxesAndFeesPriceStyles}
                                    data-testid={`cart-travelers-${ageType}-taxOrFee${viewMode}`}
                                  >
                                    {priceUtil.formatPrice(amountValue)}
                                  </span>
                                </div>
                              ),
                          )}
                        </div>
                      ),
                  )}
                </div>
              )}
              {!shouldShowTaxBreakdown(taxesAndFees) && (
                <div className={itemStyles()}>
                  <span>{priceLabel}</span>
                  <span data-testid={`cart-travelers-${ageType}-price${viewMode}`}>
                    {priceUtil.formatPrice(price)}
                  </span>
                </div>
              )}
              {!shouldShowTaxBreakdown(taxesAndFees) && isShowingPrice(markup) && (
                <div className={itemStyles()}>
                  <span data-testid={`cart-travelers-${ageType}-markup-label`}>
                    {markup >= 0 ? costLabel : discountLabel}
                  </span>
                  <span data-testid={`cart-travelers-${ageType}-markup${viewMode}`}>
                    {priceUtil.formatPrice(markup)}
                  </span>
                </div>
              )}
              {!shouldShowTaxBreakdown(taxesAndFees) && isShowingPrice(vat) && (
                <div className={itemStyles()}>
                  <span
                    className={vatLabelStyles}
                    data-testid={`cart-travelers-${ageType}-vat-label`}
                  >
                    {vatLabel}
                  </span>
                  <span
                    className={vatPriceStyles}
                    data-testid={`cart-travelers-${ageType}-vat${viewMode}`}
                  >
                    {priceUtil.formatPrice(vat)}
                  </span>
                </div>
              )}
              <div className={itemStyles(shouldShowTaxBreakdown(taxesAndFees))}>
                <span>{totalLabel}</span>
                <span data-testid={`cart-travelers-${ageType}-totalPrice${viewMode}`}>
                  {priceUtil.formatPrice(totalPrice)}
                </span>
              </div>
              {!shouldShowTaxBreakdown(taxesAndFees) && isShowingPrice(vat) && (
                <div className={itemStyles(true)}>
                  <span>{totalSumLabel}</span>
                  <span data-testid={`cart-travelers-${ageType}-totalPriceSum${viewMode}`}>
                    {priceUtil.formatPrice(totalPriceSum)}
                  </span>
                </div>
              )}
            </div>
          );
        },
      )}
    </div>
  );
};

ReceiptTravelers.propTypes = {
  dataTestId: PropTypes.string,
  totalSumLabel: PropTypes.string,
  travelerPricesInfo: PropTypes.arrayOf(
    PropTypes.shape({
      ageType: PropTypes.string,
      costLabel: PropTypes.string,
      discountLabel: PropTypes.string,
      markup: PropTypes.number,
      price: PropTypes.number,
      priceLabel: PropTypes.string,
      taxesAndFees: PropTypes.arrayOf(
        PropTypes.shape({
          category: PropTypes.string,
          taxes: PropTypes.arrayOf(
            PropTypes.shape({
              amount: PropTypes.shape({
                currency: PropTypes.shape,
                value: PropTypes.number,
              }),
              code: PropTypes.string,
              title: PropTypes.string,
            }),
          ),
        }),
      ),
      totalLabel: PropTypes.string,
      totalPrice: PropTypes.number,
      totalPriceSum: PropTypes.number,
      vat: PropTypes.number,
      vatLabel: PropTypes.string,
    }),
  ),
  viewMode: PropTypes.string,
};

export default ReceiptTravelers;
